/**
 * 🧨 Progress epoch — a one-way reset of EVERY player's progress.
 *
 * Bumping `PROGRESS_EPOCH` wipes ascension unlocks and clears, stardust,
 * owned creatures, the codex, puzzle bests, favorites, any run in flight
 * and the tutorial flag, on every device, the next time the game loads.
 *
 * ⚠️ This is irreversible and it affects everyone. Bump it only on a
 * deliberate decision (v3.79, per Javi: "I want to delete the progress of
 * everyone — everything, ascensions, stardust, puzzle game etc"), which
 * followed the economy rework: creatures were priced at 1 ⭐ until v3.62,
 * so collections bought under the old prices bore no relation to what
 * they now cost.
 *
 * WHY AN EPOCH AND NOT A ONE-OFF SCRIPT: progress lives on the device,
 * and the cloud copy is a MIRROR of it. Deleting the Firestore documents
 * alone would achieve nothing — the next device to sync would helpfully
 * upload its untouched localStorage and restore everything. Clearing
 * localStorage alone fails the same way in reverse. The epoch travels
 * WITH the synced document, so whichever side loads first, both agree
 * the old data is void (see useCloudSync's pull).
 */

/** Bump to wipe (in shared/ since v3.96 so the leaderboard submit
 * routes reject pre-wipe sessions too — the wipe's last open door).
 * Never reuse or lower a number. */
// Relative on purpose: the test bundles alias only `~`, and Nuxt
// resolves relatives everywhere.
export { PROGRESS_EPOCH } from '../../shared/progressEpoch'
import { PROGRESS_EPOCH } from '../../shared/progressEpoch'

const EPOCH_KEY = 'elemental-dispatch-epoch'

/**
 * Everything that counts as progress. Deliberately an explicit list
 * rather than a prefix sweep: `elemental-dispatch-device-id` identifies
 * the device for run-sync conflict resolution and must survive, and the
 * locale and mute switches are preferences a player set on purpose —
 * silently resetting someone's audio and language is not "progress".
 */
export const PROGRESS_KEYS = [
  'elemental-dispatch-ascension-unlocked',
  'elemental-dispatch-ascension-cleared',
  'elemental-dispatch-ascension-choice',
  'elemental-dispatch-stardust',
  'elemental-dispatch-stardust-earned',
  'elemental-dispatch-stardust-spent',
  'elemental-dispatch-owned-creatures',
  'elemental-dispatch-collection',
  'elemental-dispatch-collection-mastered',
  'elemental-dispatch-achievements',
  // 🏆 v6.45: when each achievement landed (the shelf's dates).
  'elemental-dispatch-achievements-at',
  // 🎩 v7.78: which homage awakenings have had their reveal - goes with the ledger it describes.
  'elemental-dispatch-homage-revealed',
  'elemental-dispatch-collection-viewed',
  'elemental-dispatch-favorites',
  'elemental-dispatch-favorites-at',
  'elemental-dispatch-puzzle-v1',
  'elemental-dispatch-run-v6',
  'elemental-dispatch-run-base',
  'elemental-dispatch-run-rev',
  // 👤 v7.15: which account the counters were agreed with - goes with them.
  'elemental-dispatch-run-identity',
  // 👤 v7.18: runs parked per account (`prefix:uid`) - the prefix is listed
  // for the classification test; applyProgressEpoch sweeps it by prefix.
  'elemental-dispatch-run-parked',
  'elemental-dispatch-run-stash-v1',
  // 📅 Daily Silhouette (v4.03): today's card, streak, queued boosters.
  'elemental-dispatch-daily-v1',
  // 🎲 v5.62: the Daily Expedition's run slot and the active-slot boot
  // marker — run state, so a wipe takes them (daily-v6 had slipped the
  // classification since v5.54: useRun.ts wasn't in the test's scan).
  'elemental-dispatch-daily-v6',
  'elemental-dispatch-active-slot',
  // 📊 v5.64: the lifetime stats ledger — progress, so a wipe takes it.
  'elemental-dispatch-lifetime-v1',
  // 🎖️ v5.86: the ascension-ceremony marker (a wipe re-seeds it).
  'elemental-dispatch-asc-celebrated',
  'elemental-dispatch-tutorial-done',
  'elemental-dispatch-onboarding-v1',
  'elemental-dispatch-practice-stash-v1',
  // 🎓 v7.40: the device's first steps (calm title, first run, the Shop's
  // Stardust note) - a wiped device starts over as new.
  'elemental-dispatch-first-steps-v1',
  // ✨ v6.85: the Starweave's standalone tutorial cards (a wipe re-shows them).
  'elemental-dispatch-starweave-tutorial',
  // 🧩 v7.38: the Puzzle Game's first-open card (a wipe re-shows it).
  'elemental-dispatch-puzzle-tip',
  // 🏦 v4.92: the wallet's cached server balance and queued earn events
  // describe pre-wipe value — the earn route 409s stale-epoch sessions
  // anyway, so flushing them post-wipe could only confuse the display.
  // NOTE a wipe must ALSO reset the server-side wallets collection, or
  // signed-in balances simply sync back.
  'elemental-dispatch-wallet',
  'elemental-dispatch-wallet-outbox',
  'elemental-dispatch-starweave-account-v1',
  'elemental-dispatch-starweave-pending-v1',
  'elemental-dispatch-starweave-local-v1',
]

/** Preferences and identity that a wipe must NOT touch. */
export const PRESERVED_KEYS = [
  // Moderation preference belongs to this device, not a progress epoch.
  'elemental-dispatch-hidden-players',
  'elemental-dispatch-device-id',
  'elemental-dispatch-locale',
  'elemental-dispatch-music-muted',
  'elemental-dispatch-sfx-muted',
  'elemental-dispatch-music-volume',
  'elemental-dispatch-sfx-volume',
  // ⏩ v7.42: the menu's "Tap to Fast Forward" switch - a preference, like the mutes.
  'elemental-dispatch-tap-ff-v1',
  // 🎭 v7.56: the menu's "Story scenes" switch - the same shape.
  'elemental-dispatch-story-scenes-v1',
  // 🧪 v4.92: the wallet's LOCAL-MODE switch is harness plumbing
  // (verify.mjs seeds it), not progress.
  'elemental-dispatch-wallet-local',
  // 🔍 v6.56: the shelf's review switch (?reveal-achievements=1) - a dev
  // convenience on one device, not progress.
  'elemental-dispatch-reveal-achievements',
  // 📚 v6.64: the Reference fold's dev switch (?reference=1) - same shape.
  'elemental-dispatch-reference',
  // ⚡ v6.62: "this device has seen a Google sign-in" - the lazy Firebase boot
  // reads it to boot at once; a device preference, not progress.
  'elemental-dispatch-signed-in',
  // Native recovery installation stamp and the one-time sign-in reminder.
  'elemental-dispatch-native-mirror-ready',
  'elemental-dispatch-native-signin-nudge',
  // 📈 v7.39: funnel steps this device already counted - a wipe must not re-send first-time steps.
  'elemental-dispatch-funnel-v1',
]

/** True when this device has not yet been wiped for the current epoch. */
export function wipePending(): boolean {
  if (typeof localStorage === 'undefined') return false
  return Number(localStorage.getItem(EPOCH_KEY) ?? 0) < PROGRESS_EPOCH
}

/**
 * Clears local progress and stamps the epoch. Safe to call repeatedly —
 * it no-ops once the stamp matches. Returns true if it wiped anything,
 * so the caller can decide whether to tell the player.
 */
export function applyProgressEpoch(): boolean {
  if (typeof localStorage === 'undefined') return false
  if (!wipePending()) return false
  for (const key of PROGRESS_KEYS) {
    try {
      localStorage.removeItem(key)
    } catch { /* private mode — nothing to clear anyway */ }
  }
  // 👤 v7.18: parked runs are keyed per account - swept by prefix.
  try {
    if (typeof localStorage.key === 'function') {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i)
        if (key && key.startsWith('elemental-dispatch-run-parked:')) localStorage.removeItem(key)
      }
    }
  } catch { /* private mode */ }
  try {
    localStorage.setItem(EPOCH_KEY, String(PROGRESS_EPOCH))
  } catch { /* if we cannot stamp it we will simply wipe again next load */ }
  return true
}

/** The epoch this device is stamped with (0 = never wiped). */
export function storedEpoch(): number {
  if (typeof localStorage === 'undefined') return PROGRESS_EPOCH
  return Number(localStorage.getItem(EPOCH_KEY) ?? 0)
}
